import { CoreImageView } from '@core/widgets';
import { useBehavior, useButton, useTriggerAction, useUpdateWidgetValue } from '@libs/hooks';
import { updateWidgetPropertyByPath, useAppDispatch } from '@libs/store';
import { ActionActionTypeEnum, AssetsSvgMap, JsonPathEnum, TButtonData, TCardInputData, TCommonDataObj, TWidget, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import { TCardInputOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import { getHref } from '../Image/imageUtils';
import { VisualView } from '../Image/VisualView';
import { TextCommon } from '../UI/Text/TextCommon';
import styled from './cardInput.module.css';
import { CardInputExpDate } from './CardInputExpDate';
import { CardInputNumber } from './CardInputNumber';
import { EActionName, EName, TCard, TErrorCardInputResult } from './types';
import { calcValid, getErrors } from './utils';

export const CardInput = ({ widget }: TWidgetProps<TCardInputData, TCardInputOption>) => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dispatch = useAppDispatch();
    const { updateWidgetValueByKey } = useUpdateWidgetValue(widget);
    const { triggerActionsByNameAndType } = useTriggerAction(widget);
    const { isDisabled, isHidden } = useBehavior(widget);

    const options = widget.options ?? {};
    const data = widget.data ?? {};
    const [currentErrors, setCurrentErrors] = useState<TErrorCardInputResult>(getErrors(data?.errors ?? []));
    const cardErrorWidget = { actions: currentErrors.card?.actions ?? [] } as TWidget<TButtonData>;
    const cardErrorTriggers = useTriggerAction(cardErrorWidget);
    const { handleClick } = useButton(cardErrorWidget, { isDisabled, ...cardErrorTriggers });
    const value = data.value as TCommonDataObj;
    const cardNumberValue = (value?.cardNumber as string) ?? '';
    const cardExpDateValue = (value?.expDate as string) ?? '';
    const isShowExpDateOptions = options.isShowExpDate ?? true;
    const submitNumberTextLength = data.submitNumberTextLength ?? 8;
    const isErrorCard = !!(currentErrors?.card && Object.keys(currentErrors?.card).length > 0 && currentErrors?.card?.shortText);
    const isErrorExpDate = !!(currentErrors?.expDate && Object.keys(currentErrors?.expDate).length > 0 && currentErrors?.expDate.shortText);
    const isErrorCardNumber = !!(currentErrors?.cardNumber && Object.keys(currentErrors?.cardNumber).length > 0 && currentErrors?.cardNumber.shortText);
    const isValidCardNumber = calcValid(cardNumberValue, /\s/g, 16);
    const isValidExpDate = calcValid(cardExpDateValue, /[\s/_]/g, 4);
    const isValidCard = isShowExpDateOptions ? isValidExpDate && isValidCardNumber : isValidCardNumber;
    const [isShowExpDateState, setShowExpDateState] = useState<boolean>(isShowExpDateOptions);
    const [isLoading, setLoading] = useState(false);
    const [card, setCard] = useState<TCard>({
        iconName: isValidCard && data.bankImage ? '' : options.defaultBankImage,
        avatar: isValidCard && data.bankImage ? data.bankImage : '',
        bankTitle: isValidCard && data.bankTitle ? data.bankTitle : '',
        isVisualEnabled: isValidCard,
        paymentSystemIcon: data.paymentSystemIcon,
        isShowPaymentSystem: !!(isValidCard && data.paymentSystemIcon),
    });
    const waitFor = widget.actions?.find((a) => a?.actionType === ActionActionTypeEnum.onFilled)?.delay || 1000;
    const isWidgetValid = isShowExpDateOptions
        ? isValidCardNumber && isValidExpDate && !isErrorCardNumber && !isErrorExpDate && !isErrorCard && !isLoading
        : isValidCardNumber && !isErrorCardNumber && !isErrorCard && !isLoading;

    const notifyIsCardInputValid = useEffectEvent((isValid: boolean) => {
        if (widget?.internalId) {
            dispatch(
                updateWidgetPropertyByPath({
                    uid: widget.uid,
                    value: isValid,
                    path: JsonPathEnum.IS_VALID,
                }),
            );
        }
    });

    useEffect(() => {
        notifyIsCardInputValid(isWidgetValid);
    }, [isWidgetValid]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const serverRequest = useCallback(
        async (val: string, name: string) => {
            if (val && val.length >= submitNumberTextLength) {
                setLoading(true);
                updateWidgetValueByKey(val, name);
                triggerActionsByNameAndType(ActionActionTypeEnum.onFilled, EActionName.getCardInfo, { isUpdateWidgetByUid: false })
                    .then((result) => {
                        if (result?.data) {
                            setCard({
                                iconName: result.data?.bankImage ? '' : options.defaultBankImage,
                                avatar: result.data?.bankImage ?? '',
                                bankTitle: result.data?.bankTitle ?? '',
                                isVisualEnabled: true,
                                paymentSystemIcon: result.data?.paymentSystemIcon,
                                isShowPaymentSystem: !!result.data?.paymentSystemIcon,
                            });
                            if (result?.data?.errors) {
                                const errors = getErrors(result?.data?.errors ?? []);
                                setCurrentErrors(errors);
                            }
                        }
                    })
                    .catch((e) => console.error(e))
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
        [options.defaultBankImage, submitNumberTextLength, triggerActionsByNameAndType, updateWidgetValueByKey],
    );

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const name = e.target.name;
            const inputValue = e.target.value;
            updateWidgetValueByKey(inputValue, name);
            if (isErrorCardNumber || isErrorExpDate) {
                setCurrentErrors((prevState) => {
                    return {
                        ...prevState,
                        [name]: undefined,
                    };
                });
            }
            const clearValue = inputValue?.replace(/\s/g, '') ?? '';
            if (name === EName.CARD_NUMBER && clearValue.length < submitNumberTextLength - 1) {
                setCard({
                    iconName: options.defaultBankImage,
                    avatar: '',
                    bankTitle: '',
                    isVisualEnabled: !(clearValue.length === 0),
                    paymentSystemIcon: undefined,
                    isShowPaymentSystem: false,
                });
            }
            if (name === EName.CARD_NUMBER) {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                timeoutRef.current = setTimeout(() => {
                    serverRequest(clearValue, name).then();
                }, waitFor);
            }
        },
        [updateWidgetValueByKey, serverRequest, isErrorCardNumber, isErrorExpDate, submitNumberTextLength, options.defaultBankImage, waitFor],
    );

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <div data-test={widget.uid} style={getInlineStyles({ margin: options.margin })}>
                <div className={styled.cardContainer}>
                    <div className={styled.cardRow} data-test={`${widget.uid}-bankLogoAndName`}>
                        <VisualView
                            iconName={card.iconName}
                            iconVariant={'circle'}
                            iconHierarchy={'secondary'}
                            avatar={card.avatar}
                            isDisabled={!card.isVisualEnabled}
                            rightMargin
                        />
                        <TextCommon isWidget={false} content={card.bankTitle} />
                    </div>
                    <div className={clsx(styled.cardRow, styled.alignStart)}>
                        <div className={styled.cardRowInput}>
                            <CardInputNumber
                                dataTest={`${widget.uid}-panInput`}
                                label={data.messages?.cardNumberTitle}
                                tabIndex={data.tabIndex}
                                value={cardNumberValue}
                                isShowExpDateState={isShowExpDateState}
                                isShowExpDateOptions={isShowExpDateOptions}
                                isValidCardNumber={isValidCardNumber}
                                handleChange={handleChange}
                                setShowExpDateState={setShowExpDateState}
                                isError={isErrorCardNumber}
                                errorMessage={currentErrors.cardNumber?.shortText}
                            />
                        </div>
                        {isShowExpDateState && (
                            <CardInputExpDate
                                dataTest={`${widget.uid}-expDateInput`}
                                label={data.messages?.cardExpDateTitle}
                                tabIndex={data.tabIndex ? data.tabIndex + 1 : undefined}
                                value={cardExpDateValue}
                                isError={isErrorExpDate}
                                errorMessage={currentErrors.expDate?.shortText}
                                handleChange={handleChange}
                                initialFocus={isValidCardNumber}
                            />
                        )}
                    </div>
                    <div data-test={`${widget.uid}-cardVendorLogo`} className={clsx(styled.cardRow, styled.justifyEnd)}>
                        {card.isShowPaymentSystem && (
                            <CoreImageView
                                src={getHref(card.paymentSystemIcon)}
                                styleProps={{
                                    width: 'auto',
                                    height: 'auto',
                                    iconColor: `var(--color-grey_40_lc)`,
                                }}
                            />
                        )}
                    </div>
                </div>
                <span className={styled.hintWrapper}>
                    <div data-test={`${widget.uid}-hintText`} className={clsx(styled.hintContainer, isErrorCard && styled.ieError)}>
                        {isErrorCard ? currentErrors?.card?.shortText : data.messages?.cardDescription}
                    </div>
                    {isErrorCard ? (
                        <div data-test={`${widget.uid}-hintIcon`} className={styled.iconWrapper} onClick={() => handleClick()}>
                            <CoreImageView
                                src={getHref(AssetsSvgMap.ic_info_circle_small)}
                                styleProps={{
                                    width: 16,
                                    height: 16,
                                    iconColor: `var(--color-pink_100)`,
                                }}
                            />
                        </div>
                    ) : null}
                </span>
            </div>
        </Activity>
    );
};
